import * as React from "react";
import DisplayDataPaper from "./_displayDataPaper";

import { ReactComponent as FileIcon } from "../../assets/jobIcons/file.svg";
import DisplayDataTable from "./_displayDataTable";
import { Divider, MenuItem, Select, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { ReactComponent as FilmSimple } from "../../assets/jobIcons/film-simple.svg";
import { ReactComponent as SearchIcon } from "../../assets/jobIcons/magnifying-glass.svg";
import PaginationData from "./PaginationData";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormControl from "@mui/material/FormControl";
import { useEffect, useState } from "react";
import JobInformation from "./jobs/JobInformation";
import { SelectChangeEvent } from "@mui/material";
import JobDataModal from "./modal/JobDataModal";

const columnsOrderHistory = [
  "expandIcon",
  "name",
  "id",
  // "created_at",
  "template",
  "ended_at",
  "payment_amount",
  "jobState",
];
const columnsNamesHistory = [
  "",
  "Job Name",
  "ID",
  // "Created at",
  "Template",
  "Date Ended",
  "Cost",
  "Status",
];
const columnsSortableHistory = ["payment_amount", "ended_at"];

const columnsOrderQueue = [
  "expandIcon",
  "name",
  "id",
  "template",
  "started_at",
  "jobState",
  "action",
];
const columnsNamesQueue = [
  "",
  "Job Name",
  "ID",
  "Template",
  "Date Started",
  "Status",
];
const columnsSortableQueue = ["started_at"];
const columnsProgress = ["progress"];
const columnsTemplate = ["template"];
const columnsCopy = ["id"];
const columnsCost = ["cost"];
const columnAction = ["action"];

export interface JobHistoryRecord {
  name: string;
  id: string;
  created_at: string;
  started_at: string;
  ended_at: string;
  duration: string;
  length: string;
  size: number;
  payment_amount: number;
  state: string;
  jobState: any;
  errorMessage?: string;
}

export interface JobQueueRecord {
  name: string;
  template: string;
  started_at: string;
  id: string;
  progress?: number;
  state: string;
  jobState: any;
}

export interface JobHistoryPaperProps {
  jobsHistory: any;
  jobsQueue: any;
  isLoading: boolean;
  addFilterPageNumQueue: any;
  addFilterPageNumHistory: any;
  addFilterOrderQueue: any;
  addFilterOrderHistory: any;
  limitFilters: number;
  abortJob: any;
  copyJob: any;
  deleteJob: any;
  downloadResults: any;
  canAbort: any;
  canEdit: any;
  isComplete: any;
  canDelete: any;
  onChangeJobNameQueue: any;
  onChangeJobNameHistory: any;
  runningJobsTasks: any;
  getJobDetailId: any;
  onChangeJobDetail: any;
  jobHistoryDetail: any;
  setJobsHistoryFilters: (value: any) => void;
  resultData?: any;
  jobQueueDetails?: any;
  setJobsQueueFilters: (value: any) => void;
  runJob?: any;
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString();
}

function formatFullDate(dateString: string) {
  return new Date(dateString).toLocaleString();
}
function formatCustomFullDate(date: string) {
  const newDate = new Date(date);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  const formattedDate = newDate.toLocaleString("en-US", options);
  return formattedDate;
}

// function calculateProgress(job: any, runningJobsTasks: any): number {
//   const jobId = job.id;
//   const remainingTasks =
//     runningJobsTasks && runningJobsTasks[jobId] !== undefined
//       ? +runningJobsTasks[jobId]
//       : 0;
//   const numChunks = job.numChunks ? +job.numChunks : 1;
//   if (remainingTasks >= numChunks) {
//     return 0;
//   } else {
//     //90 to not show the job completed because even if there are no more tasks
//     //there is the assembly part
//     return Math.min(
//       90,
//       Math.floor(((numChunks - remainingTasks) / numChunks) * 100)
//     );
//   }
// }

function secondsToHhMmSs(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  const secs = Math.floor(seconds - hours * 3600 - minutes * 60);

  let timeString = "";
  if (hours > 0) {
    timeString += hours + "h ";
  }
  if (minutes > 0 || hours > 0) {
    timeString += minutes + "m ";
  }
  timeString += secs + "s";

  return timeString;
}

function mindToHhMm(mins: number) {
  const hours = Math.floor(mins / 60);
  const minutes = mins - hours * 60;

  let timeString = "";
  if (hours > 0) {
    timeString += hours + "h ";
  }
  if (minutes > 0 || hours > 0) {
    timeString += minutes + "m ";
  }

  return timeString;
}

const parseJobsToJobHistoryRecords = (jobs: any[]) => {
  const parsedJobHistory: JobHistoryRecord[] = jobs.map((j) => ({
    name: j.name,
    id: j.id,
    created_at: formatDate(j.createdAt),
    started_at: formatFullDate(j.startedAt),
    ended_at: formatCustomFullDate(j.endedAt),
    template: j.templateId,
    duration:
      j.startedAt && j.completedAt
        ? secondsToHhMmSs(
            (new Date(j.completedAt).getTime() -
              new Date(j.startedAt).getTime()) /
              1000
          )
        : "0",
    length: mindToHhMm(+j.numUnitsEstimation),
    size: 0, // TODO
    payment_amount: j.paymentAmount,
    state: j.state,
    jobState: j.state.replace("JOB_STATE_", ""),
    errorMessage: j.errorMessage,
  }));

  return parsedJobHistory;
};

const parseJobsToJobQueueRecords = (jobs: any[]) => {
  const parsedJobQueue: JobQueueRecord[] = jobs.map((j) => ({
    name: j.name,
    template: j.templateId,
    started_at: j.startedAt! ? formatCustomFullDate(j.startedAt) : "-",
    id: j.id,
    state: j.state,
    jobState: j.hasPendingUpload
      ? "UPLOADING"
      : j.state.replace("JOB_STATE_", ""),
    action: j.state,
  }));

  return parsedJobQueue;
};

const JobHistoryPaper = ({ ...props }: JobHistoryPaperProps) => {
  const {
    jobsHistory,
    jobsQueue,
    addFilterPageNumHistory,
    addFilterPageNumQueue,
    addFilterOrderQueue,
    addFilterOrderHistory,
    runningJobsTasks,
  } = props;
  const {
    abortJob,
    copyJob,
    deleteJob,
    runJob,
    downloadResults,
    canAbort,
    canEdit,
    isComplete,
    canDelete,
    onChangeJobNameQueue,
    onChangeJobNameHistory,
    getJobDetailId,
    onChangeJobDetail,
    jobHistoryDetail,
    jobQueueDetails,
    isLoading,
    setJobsHistoryFilters,
    setJobsQueueFilters,
    resultData,
  } = props;

  const [openJobModal, setOpenJobModal] = useState(false);
  const [objectViewData, setObjectViewData] = useState({});
  const [currentPageSizeHistory, setCurrentPageSizeHistory] =
    useState<number>(5);
  const [currentPageSizeQueue, setCurrentPageSizeQueue] = useState<number>(5);
  const pageSize = [5, 10, 20, 50, 100];

  const handleClearJobDetail = () => {
    onChangeJobDetail({});
  };
  const onPageSizeChange = (e: SelectChangeEvent<number>) => {
    setJobsHistoryFilters((prev: { limit: number; offset: number }) => ({
      ...prev,
      offset: 0,
      limit: e.target.value,
    }));
    setCurrentPageSizeHistory(e.target.value as number);
  };
  const onQueuePageSizeChange = (e: SelectChangeEvent<number>) => {
    setJobsQueueFilters((prev: { limit: number; offset: number }) => ({
      ...prev,
      offset: 0,
      limit: e.target.value,
    }));
    setCurrentPageSizeQueue(e.target.value as number);
  };

  const handlePageNumberClick = (page: number) => {
    const offset = (page - 1) * currentPageSizeHistory;
    setJobsHistoryFilters((prev: { limit: number; offset: number }) => ({
      ...prev,
      offset: offset,
    }));
  };
  const handleQueuePageNumberClick = (page: number) => {
    const offset = (page - 1) * currentPageSizeQueue;
    setJobsQueueFilters((prev: { limit: number; offset: number }) => ({
      ...prev,
      offset: offset,
    }));
  };

  const JobNameFilter = (onChangeJobName: any) => (
    <Box sx={{ mx: 1 }}>
      <FormControl sx={{ m: 1 }} fullWidth>
        <OutlinedInput
          startAdornment={
            <Box sx={{ mx: 1 }}>
              <SearchIcon></SearchIcon>
            </Box>
          }
          color={"primary"}
          autoComplete="off"
          placeholder="Search job"
          onChange={(event) => onChangeJobName(event.target.value)}
        />
      </FormControl>
    </Box>
  );
  const handleViewObject = (data: any) => {
    setOpenJobModal(true);
    setObjectViewData(data);
  };
  const closeJobModal = () => {
    setOpenJobModal(false);
    setObjectViewData({});
  };
  const [clickCount, setClickCount] = useState(0);

  const handleClickMe = () => {
    setClickCount((prev) => (prev + 1) % 3); // Loop back to 0 after 2
  };
  const DisplayData = (
    data: any[],
    iconNoData: any,
    messageNoData: string,
    columnsNames: string[],
    columnsOrder: string[],
    columnsSortable: string[],
    onColumnClick: any,
    columnsProgress: string[],
    columnsTemplate?: string[],
    columnsCopy?: string[],
    columnsCost?: string[],
    ExpandComponent?: any,
    JobDetails?: any,
    tableInfo?: string,
    columnAction?: string[]
  ) => (
    <DisplayDataTable
      data={data}
      columnsNames={columnsNames}
      columnsOrder={columnsOrder}
      columnsSortable={columnsSortable}
      abortJob={abortJob}
      copyJob={copyJob}
      deleteJob={deleteJob}
      downloadResults={downloadResults}
      canAbort={canAbort}
      canEdit={canEdit}
      canDelete={canDelete}
      isComplete={isComplete}
      hasJobActions={true}
      iconNoData={iconNoData}
      displayTableIfNoData={true}
      messageNoData={messageNoData}
      columnsJobState={["jobState"]}
      onColumnClick={onColumnClick}
      columnsProgress={columnsProgress}
      columnsTemplate={columnsTemplate}
      columnsCopy={columnsCopy}
      columnsCost={columnsCost}
      columnsAction={columnAction}
      ExpandComponent={ExpandComponent}
      getJobId={getJobDetailId}
      // clearJobDetail={handleClearJobDetail}
      jobDetailData={JobDetails}
      isLoading={isLoading}
      resultData={resultData}
      viewObject={handleViewObject}
      currentPageSize={0}
      tableInfo={tableInfo}
      runJob={runJob}
    />
  );

  return (
    <Box>
      <DisplayDataPaper
        title={"Job Queue"}
        headerActions={
          <Stack direction={"row"} alignItems={"center"} columnGap={1}>
            <Typography>Show</Typography>
            <Select
              value={currentPageSizeQueue}
              onChange={onQueuePageSizeChange}
              sx={{
                pt: "10px",
              }}
            >
              {pageSize.map((v, i) => (
                <MenuItem key={i} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        }
        children={DisplayData(
          parseJobsToJobQueueRecords(
            jobsQueue && jobsQueue.data ? jobsQueue.data : []
          ),
          <FilmSimple></FilmSimple>,
          "Nothing in queue... start now",
          columnsNamesQueue,
          columnsOrderQueue,
          columnsSortableQueue,
          addFilterOrderQueue,
          columnsProgress,
          columnsTemplate,
          columnsCopy,
          columnsCost,
          <JobInformation isLoading={isLoading} data={jobQueueDetails} />,
          jobQueueDetails,
          "queue",
          columnAction
        )}
      ></DisplayDataPaper>
      <PaginationData
        limit={currentPageSizeQueue}
        pagination={jobsQueue.pagination}
        onPageNumberClick={handleQueuePageNumberClick}
      ></PaginationData>
      <Divider
        className={"divider-vertical-paper-large-no-top-margin"}
      ></Divider>
      <DisplayDataPaper
        title={"Job History"}
        headerActions={
          <Stack direction={"row"} alignItems={"center"} columnGap={1}>
            <Typography>Show</Typography>
            <Select
              value={currentPageSizeHistory}
              onChange={onPageSizeChange}
              sx={{
                pt: "10px",
              }}
            >
              {pageSize.map((v, i) => (
                <MenuItem key={i} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        }
        children={DisplayData(
          parseJobsToJobHistoryRecords(
            jobsHistory && jobsHistory.data ? jobsHistory.data : []
          ),
          <FileIcon></FileIcon>,
          "Nothing in the job history so far",
          columnsNamesHistory,
          columnsOrderHistory,
          columnsSortableHistory,
          addFilterOrderHistory,
          [],
          columnsTemplate,
          columnsCopy,
          columnsCost,
          <JobInformation
            isLoading={isLoading}
            data={jobHistoryDetail}
            resultData={resultData}
            onResultClick={downloadResults}
          />,
          jobHistoryDetail,
          "history"
        )}
      ></DisplayDataPaper>
      <Box my={1}>
        <PaginationData
          limit={currentPageSizeHistory}
          pagination={jobsHistory.pagination}
          onPageNumberClick={handlePageNumberClick}
        ></PaginationData>
      </Box>
      <JobDataModal
        openModal={openJobModal}
        handleClose={closeJobModal}
        title={""}
      >
        {objectViewData}
      </JobDataModal>
    </Box>
  );
};

export default JobHistoryPaper;
